/** @format */

import TuneIcon from '@mui/icons-material/Tune';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import { APPLICATION_STAGE } from '../../../common/models/enums/applicationStage';
import { baseURL } from '../../../config/AxiosCall';
import { sessions } from '../../../common/localstorage/SessionStorage';

type AdvancedExportPayload = {
  year: number;
  yearOfStudyIds: number[];
  stages: number[];
};

/** Matches seed IDs in YearOfStudy (1st Year … CTA/PGDA). */
const YEAR_OF_STUDY_OPTIONS: { id: number; label: string }[] = [
  { id: 1, label: '1st Year' },
  { id: 2, label: '2nd Year' },
  { id: 3, label: '3rd Year' },
  { id: 4, label: '4th Year' },
  { id: 5, label: 'CTA/PGDA' },
];

const STAGE_OPTIONS: { value: APPLICATION_STAGE; label: string }[] = [
  { value: APPLICATION_STAGE.All, label: 'All (any stage)' },
  { value: APPLICATION_STAGE.NotAssessed, label: 'Not assessed (flag)' },
  { value: APPLICATION_STAGE.Assessed, label: 'Assessed (flag)' },
  { value: APPLICATION_STAGE.PersonalDetails, label: 'Personal details' },
  { value: APPLICATION_STAGE.AddressDetails, label: 'Address details' },
  { value: APPLICATION_STAGE.InstitutionDetails, label: 'Institution details' },
  { value: APPLICATION_STAGE.StudyDetails, label: 'Study details' },
  { value: APPLICATION_STAGE.LegalGuardianDetails, label: 'Legal guardian details' },
  { value: APPLICATION_STAGE.DocumentsUpload, label: 'Documents upload' },
  { value: APPLICATION_STAGE.ApplicationSubmitted, label: 'Application submitted' },
  { value: APPLICATION_STAGE.ApplicationInReview, label: 'Application in review' },
  { value: APPLICATION_STAGE.ApplicationDeclined, label: 'Application declined' },
  {
    value: APPLICATION_STAGE.ApplicationSentForFurtherReview,
    label: 'Application sent for further review',
  },
  {
    value: APPLICATION_STAGE.ApplicationFullyAssessedAndIsPendingUniversitySelection,
    label: 'Pending university selection',
  },
  { value: APPLICATION_STAGE.ApplicationSuccessful, label: 'Application successful' },
  { value: APPLICATION_STAGE.IncompleteApplication, label: 'Incomplete application' },
];

type Props = {
  open: boolean;
  onClose: () => void;
  year: number;
  hasApplications: boolean;
};

export default function AdvancedExportApplicationsModal({
  open,
  onClose,
  year,
  hasApplications,
}: Props) {
  const [selectedStages, setSelectedStages] = useState<typeof STAGE_OPTIONS>([]);
  const [selectedYearsOfStudy, setSelectedYearsOfStudy] =
    useState<typeof YEAR_OF_STUDY_OPTIONS>([]);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const stageOptions = useMemo(() => STAGE_OPTIONS, []);
  const yearOfStudyOptions = useMemo(() => YEAR_OF_STUDY_OPTIONS, []);

  useEffect(() => {
    if (open) {
      setSelectedStages([]);
      setSelectedYearsOfStudy([]);
      setError(null);
    }
  }, [open]);

  const handleExport = async () => {
    setError(null);
    if (!selectedStages.length) {
      setError('Select at least one application stage.');
      return;
    }

    const payload: AdvancedExportPayload = {
      year,
      yearOfStudyIds: selectedYearsOfStudy.map((y) => y.id),
      stages: selectedStages.map((o) => o.value as number),
    };

    setExporting(true);
    try {
      const response = await axios.post(
        `${baseURL}ApplicationManagement/export-to-excel-advanced`,
        payload,
        {
          responseType: 'blob',
          headers: {
            Authorization: `Bearer ${sessions.getToken()}`,
            Accept:
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'Content-Type': 'application/json',
          },
          validateStatus: () => true,
        }
      );

      const contentType =
        (response.headers['content-type'] as string) || '';

      if (contentType.includes('application/json')) {
        const text = await (response.data as Blob).text();
        try {
          const body = JSON.parse(text) as {
            validation?: { messages?: string[] };
          };
          const msg =
            body.validation?.messages?.join(' ') ??
            'Export could not be completed.';
          setError(msg);
        } catch {
          setError(text || 'Export failed.');
        }
        return;
      }

      if (response.status >= 400) {
        setError('Export failed. Please try again.');
        return;
      }

      const url = window.URL.createObjectURL(
        new Blob([response.data as BlobPart])
      );
      const link = document.createElement('a');
      const timestamp = new Date().toISOString().split('T')[0];
      link.href = url;
      link.setAttribute(
        'download',
        `Applicants-Advanced-${year}-${timestamp}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      onClose();
    } catch (e) {
      console.error(e);
      setError('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const allStudyYearsSelected =
    selectedYearsOfStudy.length === YEAR_OF_STUDY_OPTIONS.length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TuneIcon color="primary" />
        Advanced export
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Application year: <strong>{year}</strong> (from the toolbar). Choose one
            or more <strong>stages</strong>; rows match if they match{' '}
            <em>any</em> selected stage. Optionally limit by the same{' '}
            <strong>year of study</strong> as the export column (study details when
            present, otherwise education; PGDA/CTA is on study details). Leave empty
            for <em>all</em> years, including applicants with no year set.
          </Typography>

          <Autocomplete
            multiple
            options={stageOptions}
            getOptionLabel={(o) => o.label}
            value={selectedStages}
            onChange={(_, v) => setSelectedStages(v)}
            disableCloseOnSelect
            renderInput={(params) => (
              <TextField
                {...params}
                label="Application stages"
                placeholder="Search or select stages…"
              />
            )}
          />

          <Box>
            <Autocomplete
              multiple
              options={yearOfStudyOptions}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              value={selectedYearsOfStudy}
              onChange={(_, v) => setSelectedYearsOfStudy(v)}
              disableCloseOnSelect
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Year of study (optional)"
                  placeholder="All — leave empty, or choose one or more…"
                />
              )}
            />
            <Button
              size="small"
              sx={{ mt: 1 }}
              onClick={() =>
                setSelectedYearsOfStudy((prev) =>
                  prev.length === YEAR_OF_STUDY_OPTIONS.length
                    ? []
                    : [...YEAR_OF_STUDY_OPTIONS]
                )
              }
              disabled={exporting}>
              {allStudyYearsSelected
                ? 'Clear year filter'
                : 'Select all study years'}
            </Button>
          </Box>

          {error && (
            <Box sx={{ color: 'error.main', typography: 'body2' }}>{error}</Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={exporting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleExport}
          disabled={exporting || !hasApplications}
          startIcon={
            exporting ? <CircularProgress size={18} color="inherit" /> : null
          }>
          {exporting ? 'Exporting…' : 'Export'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
